// Trigger-surface analysis: how likely is Claude to pick this skill up?
// Inspired by the guide's "description is the most important field" section.

export interface TriggerMetrics {
  quotedPhrases: string[];
  cuePhrases: string[];
  fileTypes: string[];
  toolMentions: string[];
  negativeTriggers: string[];
  domainTerms: string[];
  triggerDensity: number;        // distinct triggers per 100 chars
  distinctTriggerCount: number;
  hasMultipleTriggers: boolean;
  hasNegativeTrigger: boolean;
  mentionsFileTypes: boolean;
  isOverlyGeneric: boolean;
}

const CUE_PATTERNS: RegExp[] = [
  /\buse when\b/gi,
  /\bwhen (?:the )?user\b/gi,
  /\bwhen working with\b/gi,
  /\bwhen building\b/gi,
  /\bwhen creating\b/gi,
  /\basks? (?:to|about|for)\b/gi,
  /\brequests?\b/gi,
  /\bwants? to\b/gi,
  /\bsays?\b/gi,
  /\bmentions?\b/gi,
  /\binvoke(?:d)? (?:when|for)\b/gi,
  /\btriggers? on\b/gi,
  /\bfor (?:tasks?|workflows?|requests?)\b/gi,
];

const NEGATIVE_PATTERNS: RegExp[] = [
  /\bdo(?:es)? NOT use\b/i,
  /\bdon'?t use\b/i,
  /\bnot for\b/i,
  /\bavoid (?:using|invoking) (?:this|the) skill\b/i,
  /\bdo not (?:apply|trigger|invoke)\b/i,
  /\bnever (?:use|invoke|apply)\b/i,
  /\bnot intended\b/i,
];

// Common explicit file-type tokens that signal I/O domain.
const FILE_TYPE = /\.(pdf|docx?|pptx?|xlsx?|csv|tsv|json|ya?ml|html?|md|tsx?|jsx?|py|rs|go|rb|sh|sql|svg|png|jpe?g|gif|webp|mp[34]|wav|fig|psd|sketch|zip|tar|gz)\b/gi;

// Tools/services that should appear by name when relevant.
const TOOL_HINT = /\b(?:Notion|Linear|Figma|Slack|Discord|GitHub|GitLab|Jira|Asana|Trello|Sentry|Datadog|Zapier|Airtable|Salesforce|HubSpot|Stripe|Square|Calendly|Google\s*(?:Drive|Docs|Sheets|Slides|Calendar)|Microsoft\s*(?:Word|Excel|Powerpoint|Teams)|AWS|GCP|Azure|Heroku|Vercel|Netlify|Cloudflare|MongoDB|Postgres|Redis|Kafka|Kubernetes|Docker|Terraform|Ansible|Playwright|Cypress|Storybook|Webpack|Vite|Rollup|Babel|ESLint|Prettier|Anthropic|OpenAI|Claude|GPT|MCP)\b/g;

const GENERIC_SIGNALS = [
  /^helps with everything/i,
  /^does many things/i,
  /\banything you want\b/i,
  /\bany (?:task|thing|workflow)\b/i,
  /\ball of your\b/i,
  /\bgeneral.purpose\b/i,
];

function extractQuoted(text: string): string[] {
  const out: string[] = [];
  const re = /["'`]([^"'`]{3,80})["'`]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    out.push(m[1].trim());
  }
  return out;
}

function extractCues(text: string): string[] {
  const out = new Set<string>();
  for (const re of CUE_PATTERNS) {
    const matches = text.match(re);
    if (matches) for (const m of matches) out.add(m.toLowerCase().trim());
  }
  return [...out];
}

function extractFileTypes(text: string): string[] {
  const out = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = FILE_TYPE.exec(text)) !== null) {
    out.add(m[0].toLowerCase());
  }
  return [...out];
}

function extractTools(text: string): string[] {
  const out = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = TOOL_HINT.exec(text)) !== null) {
    out.add(m[0]);
  }
  return [...out];
}

function extractNegativeTriggers(text: string): string[] {
  const out: string[] = [];
  for (const re of NEGATIVE_PATTERNS) {
    const m = text.match(re);
    if (m) out.push(m[0]);
  }
  return out;
}

function extractDomainTerms(text: string): string[] {
  // Heuristic: lowercase tokens of length >= 4 that aren't in a small stopword list.
  // Plus any TitleCase/UPPERCASE tokens that look like domain names.
  const stop = new Set([
    'this','that','these','those','with','from','into','onto','your','their','them',
    'they','have','will','want','want to','need','user','users','skill','skills',
    'when','where','what','about','using','used','than','also','more','most','some',
    'each','many','only','very','here','there','then','through','across','between',
    'such','like','asks','say','says','help','helps','make','use','uses','for','and',
    'or','but','not','its','can','should','may','must','any','all','one','two','three',
  ]);
  const set = new Set<string>();
  for (const m of text.match(/\b[A-Za-z][A-Za-z0-9-]{3,}\b/g) ?? []) {
    if (!stop.has(m.toLowerCase()) && m.length <= 24) set.add(m.toLowerCase());
  }
  return [...set];
}

export function analyzeTriggers(text: string): TriggerMetrics {
  const quoted = extractQuoted(text);
  const cues = extractCues(text);
  const fileTypes = extractFileTypes(text);
  const tools = extractTools(text);
  const negatives = extractNegativeTriggers(text);
  const domain = extractDomainTerms(text);

  const distinctTriggers = new Set<string>([
    ...quoted.map((q) => q.toLowerCase()),
    ...cues,
    ...fileTypes,
    ...tools.map((t) => t.toLowerCase()),
  ]);
  const distinctTriggerCount = distinctTriggers.size;
  const triggerDensity = text.length === 0 ? 0 : (distinctTriggerCount / text.length) * 100;
  const isOverlyGeneric = GENERIC_SIGNALS.some((re) => re.test(text));

  return {
    quotedPhrases: quoted,
    cuePhrases: cues,
    fileTypes,
    toolMentions: tools,
    negativeTriggers: negatives,
    domainTerms: domain,
    triggerDensity,
    distinctTriggerCount,
    hasMultipleTriggers: distinctTriggerCount >= 3,
    hasNegativeTrigger: negatives.length > 0,
    mentionsFileTypes: fileTypes.length > 0,
    isOverlyGeneric,
  };
}
